import React from "react";
import {useSetting} from "../../contexts/setting";
import type {PixelationDirection} from "../../libs/pixelation";

// The fade direction each click rotates to next, and how to describe it. The
// arrow icon points along the fade direction, so rotating the SVG by these
// angles (clockwise from "points right") keeps it in sync with the mosaic.
const ANGLE: Record<PixelationDirection, number> = {ltr: 0, ttb: 90, rtl: 180, btt: 270};
export const PIXELATION_DIRECTION_LABELS: Record<PixelationDirection, string> = {
  ltr: "left to right",
  ttb: "top to bottom",
  rtl: "right to left",
  btt: "bottom to top",
};

/**
 * Round rotate button inside the per-template mosaic popover (PixelSizeControl),
 * shown for unchained (locked) Releases templates while the overlay is on. Each
 * click rotates that template's mosaic 90° clockwise (ltr → ttb → rtl → btt →
 * ltr), stored per template in `pixelationDirectionOverrides`. The arrow points
 * along the current fade direction. Every template starts at the global
 * `pixelationDirection` (the panel's "From").
 */
const PixelDirectionControl = ({id}: {id: string}) => {
  const {pixelationDirectionOverrides, pixelationDirection, cyclePixelationDirection} =
    useSetting();

  const direction: PixelationDirection = pixelationDirectionOverrides?.[id] || pixelationDirection;
  const isRotated = direction !== pixelationDirection;

  return (
    <button
      type="button"
      className={`checkbox pixel-rotate${isRotated ? " active" : ""}`}
      data-tooltip={`Rotate mosaic direction (now: ${PIXELATION_DIRECTION_LABELS[direction]})`}
      aria-label={`Rotate mosaic direction for this template (currently ${PIXELATION_DIRECTION_LABELS[direction]})`}
      onClick={() => cyclePixelationDirection(id)}
    >
      <svg
        width="20"
        height="20"
        viewBox="0 0 20 20"
        fill="none"
        aria-hidden="true"
        style={{transform: `rotate(${ANGLE[direction]}deg)`}}
      >
        {/* Arrow pointing right — the fade direction at 0° ("ltr"). */}
        <path d="M3 10h11" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
        <path
          d="M11 6l4 4-4 4"
          stroke="currentColor"
          strokeWidth="1.6"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    </button>
  );
};

export default PixelDirectionControl;
